import { Nullable } from "@/types";

export enum Pressure {
  hPA = "hPA",
  inHg = "inHg",
  mmHg = "mmHg",
}

export enum Temperature {
  celsius = "celsius",
  fahrenheit = "fahrenheit",
  kelvin = "kelvin",
}

export enum WindSpeed {
  kts = "kts",
  kmh = "kmh",
  ms = "ms",
}

export enum AltitudeUnit {
  meters = "meters",
  feet = "feet",
}

export class PressureConverter {
  private static toInHg(value: number, backendUnit: string): number {
    switch (backendUnit) {
      case "inHg":
        return value;
      case "hPa":
      case "mb":
        return value * 0.02953;
      default:
        return 0;
    }
  }

  static fromUnit(
    value: number,
    backendUnit: string,
    targetUnit: Pressure
  ): number {
    const inHgPressure = this.toInHg(value, backendUnit);

    switch (targetUnit) {
      case Pressure.hPA:
        return inHgPressure * 33.8639;
      case Pressure.inHg:
        return inHgPressure;
      case Pressure.mmHg:
        return inHgPressure * 25.4;
      default:
        return 0;
    }
  }

  static fromString(name: string): Pressure {
    const normalizedName = name.toLowerCase();
    const values = Object.values(Pressure) as string[];
    const found = values.find((item) => item.toLowerCase() === normalizedName);
    return (found as Pressure) || Pressure.hPA;
  }

  static toString(unit: Pressure): string {
    switch (unit) {
      case Pressure.hPA:
        return "hPA";
      case Pressure.inHg:
        return "inHg";
      case Pressure.mmHg:
        return "mmHg";
      default:
        return "hPA";
    }
  }
}

export class TemperatureConverter {
  private static toFahrenheit(value: number, backendUnit: string): number {
    switch (backendUnit) {
      case "°C":
      case "C":
        return (value * 9) / 5 + 32;
      case "°F":
      case "F":
        return value;
      default:
        return 0;
    }
  }

  static fromUnit(
    value: number,
    backendUnit: string,
    targetUnit: Temperature
  ): number {
    const fahrenheitValue = this.toFahrenheit(value, backendUnit);

    switch (targetUnit) {
      case Temperature.celsius:
        return ((fahrenheitValue - 32) * 5) / 9;
      case Temperature.fahrenheit:
        return fahrenheitValue;
      case Temperature.kelvin:
        return ((fahrenheitValue - 32) * 5) / 9 + 273.15;
      default:
        return 0;
    }
  }

  static fromString(name: string): Temperature {
    const normalizedName = name.toLowerCase();
    const values = Object.values(Temperature) as string[];
    const found = values.find((item) => item.toLowerCase() === normalizedName);
    return (found as Temperature) || Temperature.celsius;
  }

  static toString(unit: Temperature): string {
    switch (unit) {
      case Temperature.celsius:
        return "°C";
      case Temperature.fahrenheit:
        return "°F";
      case Temperature.kelvin:
        return "K";
      default:
        return "°C";
    }
  }
}

export class WindSpeedConverter {
  private static toKnots(value: number, backendUnit: string): number {
    switch (backendUnit) {
      case "kt":
      case "kts":
        return value;
      case "km/h":
      case "kmh":
        return value * 0.539957;
      case "mph":
        return value * 0.868976;
      default:
        return 0;
    }
  }

  static fromUnit(
    value: number,
    backendUnit: string,
    targetUnit: WindSpeed
  ): number {
    const knots = this.toKnots(value, backendUnit);

    switch (targetUnit) {
      case WindSpeed.kts:
        return knots;
      case WindSpeed.kmh:
        return knots * 1.852;
      case WindSpeed.ms:
        return knots * 0.514444;
      default:
        return 0;
    }
  }

  static fromString(name: string): WindSpeed {
    const normalizedName = name.toLowerCase();
    const values = Object.values(WindSpeed) as string[];
    const found = values.find((item) => item.toLowerCase() === normalizedName);
    return (found as WindSpeed) || WindSpeed.kmh;
  }

  static toString(unit: WindSpeed): string {
    switch (unit) {
      case WindSpeed.kts:
        return "kts";
      case WindSpeed.kmh:
        return "km/h";
      case WindSpeed.ms:
        return "m/s";
      default:
        return "km/h";
    }
  }
}

export class AltitudeUnitConverter {
  private static toFeet(value: number, backendUnit: string): number {
    switch (backendUnit) {
      case "ft":
      case "feet":
        return value;
      case "m":
      case "meters":
        return value / 0.3048;
      default:
        return 0;
    }
  }

  static fromUnit(
    value: number,
    backendUnit: string,
    targetUnit: AltitudeUnit
  ): number {
    const feetValue = this.toFeet(value, backendUnit);

    switch (targetUnit) {
      case AltitudeUnit.meters:
        return feetValue * 0.3048;
      case AltitudeUnit.feet:
        return feetValue;
      default:
        return 0;
    }
  }

  static fromString(name: string): AltitudeUnit {
    const normalizedName = name.toLowerCase();
    const values = Object.values(AltitudeUnit) as string[];
    const found = values.find((item) => item.toLowerCase() === normalizedName);
    return (found as AltitudeUnit) || AltitudeUnit.feet;
  }

  static toString(unit: AltitudeUnit): string {
    switch (unit) {
      case AltitudeUnit.meters:
        return "meters";
      case AltitudeUnit.feet:
        return "ft";
      default:
        return "ft";
    }
  }
}

export const formatTemperature = (
  temp: Nullable<number>,
  unit: Nullable<string>
): string => {
  if (temp == null || !unit) return "";
  const tempValue = temp;
  const backendUnit = unit;
  const tempInCelsius = TemperatureConverter.fromUnit(
    tempValue,
    backendUnit,
    Temperature.celsius
  );
  const tempInFahrenheit = TemperatureConverter.fromUnit(
    tempValue,
    backendUnit,
    Temperature.fahrenheit
  );
  const tempInCelsiusRounded = Math.round(tempInCelsius);
  const tempInFahrenheitRounded = Math.round(tempInFahrenheit);
  return `${tempInCelsiusRounded}°C / ${tempInFahrenheitRounded}°F`;
};

export const formatCoordinates = (
  lat: Nullable<number>,
  lng: Nullable<number>,
  decimals: number = 2
): string => {
  if (lat == null || lng == null) return "";

  const latDirection = lat >= 0 ? "N" : "S";
  const lngDirection = lng >= 0 ? "E" : "W";

  const formattedLat = Math.abs(lat).toFixed(decimals);
  const formattedLng = Math.abs(lng).toFixed(decimals);

  return `${formattedLat}${latDirection}/${formattedLng}${lngDirection}`;
};
